/**
 * The observation clock (M95, the user's brief, `docs/AQUARIUM_REDESIGN.md`
 * section 6.2): the hour the aquarium is drawn at, apart from the game's
 * calendar. The calendar runs on at five seconds a day; the aquarium stays at
 * the hour the player chose (Noon unless they choose otherwise) while the
 * date still sets the season, the sun's path and which animals are there.
 * It drives the light and the animals' daily phase and changes nothing in the
 * world: the simulation never reads it (`src/sim` imports nothing from
 * `src/ui`).
 *
 * Its hour is local solar time, 0 to 24, noon at 12, as the fish's day reads
 * it (`fishSwim.ts`). A preset's change runs the hour on toward it, forward
 * in time as a day goes, at `HOURS_PER_S`, so the light and the animals move
 * there smoothly and never in a snap. Pure, tested in node
 * (`tests/observation.test.ts`).
 */
import { dateLabel } from '../sim/calendar';

export type Preset = 'dawn' | 'noon' | 'dusk' | 'night';

export const PRESETS: readonly Preset[] = ['dawn', 'noon', 'dusk', 'night'];

/** Each preset's name as the player reads it. */
export const PRESET_NAMES: Readonly<Record<Preset, string>> = { dawn: 'Dawn', noon: 'Noon', dusk: 'Dusk', night: 'Night' };

/**
 * How fast the hour runs to a new preset, hours a second of the player's time (JUDGED: from noon to dusk at Maui
 * Nui, 5.4 hours on its shortest day, in 4.5 seconds, and noon to midnight in 10; at that pace the reef's
 * schools swim their way home at a quarter of the pace they did on M92's five-second day, 4.8 hours a second).
 */
export const HOURS_PER_S = 1.2;

/**
 * The hour a preset shows on a day `dayLengthHours` long: Dawn at sunrise, Noon at noon, Dusk at sunset, Night at
 * midnight. Where the sun never sets, sunrise and sunset are both midnight; where it never rises, both noon.
 */
export function presetHour(preset: Preset, dayLengthHours: number): number {
  const half = Math.min(12, Math.max(0, dayLengthHours / 2));
  switch (preset) {
    case 'dawn': return (12 - half) % 24;
    case 'noon': return 12;
    case 'dusk': return (12 + half) % 24;
    case 'night': return 0;
  }
}

/** An hour as a clock reads it, American style: "12:00 PM", "6:33 AM". */
export function clockLabel(hour: number): string {
  const minutes = Math.round((((hour % 24) + 24) % 24) * 60) % 1440;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/**
 * The observation panel's line (M104, the brief's example: "12 June 2034 · observing at 12:00 PM · Maui Nui"): the
 * game's date, the aquarium's hour apart from it, and the place drawn.
 */
export function observationLine(epochDay: number, hour: number, placeName: string): string {
  return `${dateLabel(epochDay)} · observing at ${clockLabel(hour)} · ${placeName.replace(/ Sanctuary$/, '')}`;
}

/**
 * Watch One Day (M96, the brief's section 6.4): a whole day in this many real seconds, midnight to midnight, the
 * simulation held still (the brief's 90 seconds; its markers, sunrise at 22.5 s for a sun rising at 6:00, fall
 * where the day's own sunrise and sunset put them).
 */
export const PLAYBACK_S = 90;

/**
 * The lengths a watched day may be given (M104, the brief's section 13.4: "The playback's 90-second duration should
 * be the default. Changing this presentation duration must never change ecological rates"): half the default, the
 * default, and two and four times it (JUDGED, a rendering choice: slower days to watch a dusk at leisure). Nothing
 * ecological runs while a day is watched, so its length changes only its pace.
 */
export const PLAYBACK_LENGTHS_S: readonly number[] = [45, PLAYBACK_S, 180, 360];

/** Where the camera stands and what it looks at, scene units. */
export interface CameraView {
  position: readonly [number, number, number];
  target: readonly [number, number, number];
  /** Whether it is the player's own, not the habitat's frame (the scene's to say). */
  own?: boolean;
}

/**
 * A Watch One Day under way: its seconds watched, whether it is playing, whether the camera is locked on what it
 * frames (M104, the brief's: "lock the camera on a habitat or animal for the full 90 seconds"), and what to give back
 * on its end: its preset, and the camera's view at its start until the player moves the camera (M104, the brief's
 * Exit: "restore the previous view ... unless the player explicitly chose otherwise").
 */
export interface Watching {
  elapsed: number;
  playing: boolean;
  locked: boolean;
  restore: { preset: Preset; camera: CameraView | null };
}

export class ObservationClock {
  /** The hour drawn now. */
  hour = 12;
  /** The preset chosen, and the hour it shows today. */
  preset: Preset = 'noon';
  private target = 12;
  /** Watch One Day, while it runs (M96); null otherwise. */
  watching: Watching | null = null;
  /** The real seconds a watched day takes (M104): `PLAYBACK_S` unless the player chose another. Not saved. */
  length = PLAYBACK_S;

  /**
   * Starts Watch One Day at local midnight, keeping the preset and the camera's view `camera` to give back (M96, M104)
   * and the camera's lock (M104). A second start replays it, what it gives back the first's.
   */
  watch(camera: CameraView | null = null): void {
    this.watching = { elapsed: 0, playing: true, locked: this.watching?.locked ?? false, restore: this.watching?.restore ?? { preset: this.preset, camera } };
    this.hour = 0;
  }

  /** The player moved the camera during the watched day (turned, zoomed, or followed an animal): its view is theirs, not given back. */
  cameraMoved(): void {
    if (this.watching) this.watching.restore.camera = null;
  }

  /** A watched day's length, one of `PLAYBACK_LENGTHS_S`; a day under way keeps its hour, its pace changed. */
  setLength(seconds: number): void {
    if (!PLAYBACK_LENGTHS_S.includes(seconds)) return;
    if (this.watching) this.watching.elapsed *= seconds / this.length;
    this.length = seconds;
  }

  /** Locks the camera on what it frames for the rest of the watched day, or frees it. */
  setLocked(locked: boolean): void {
    if (this.watching) this.watching.locked = locked;
  }

  /** Pauses or resumes the day being watched; the simulation stays held either way. */
  setPlaying(playing: boolean): void {
    if (this.watching) this.watching.playing = playing;
  }

  /**
   * Ends Watch One Day: the preset it began from given back, the hour running on to it from where the day stopped;
   * the camera's view at its start returned for the caller to give back, null where the player moved it or none.
   */
  stopWatching(dayLengthHours: number): CameraView | null {
    if (!this.watching) return null;
    const { preset, camera } = this.watching.restore;
    this.watching = null;
    this.choose(preset, dayLengthHours);
    return camera;
  }

  /** How far through the watched day, 0 to 1; null when none is being watched. */
  progress(): number | null {
    return this.watching ? this.watching.elapsed / this.length : null;
  }

  /** Chooses a preset on a day `dayLengthHours` long; the hour runs on to it (`advance`). */
  choose(preset: Preset, dayLengthHours: number): void {
    this.preset = preset;
    this.target = presetHour(preset, dayLengthHours);
  }

  /**
   * Keeps the preset on the day's own hour as the days go (a sunrise a minute earlier tomorrow). Once there, the
   * hour moves with it, a minute back or forward, never round the whole day.
   */
  follow(dayLengthHours: number): void {
    const next = presetHour(this.preset, dayLengthHours);
    // Arrived, or the day's change has moved the hour's target back past it (between the new target and the old,
    // going forward): there now, never on round the whole day to it (M95's review: on Maui Nui's shortening days
    // at 128×, 4 of 200 changes to Dusk ran a whole day more).
    const back = mod24(this.target - next);
    const passed = back > 0 && back < 12 && mod24(this.hour - next) < back;
    if ((this.hour === this.target || passed) && !this.watching) this.hour = next;
    this.target = next;
  }

  /**
   * Runs the hour on by `seconds` of the player's time; the hour drawn. While a day is watched, the watched day's
   * hour, `length` real seconds to 24 hours, whatever the frame rate, held at the next midnight at its end;
   * else on toward its preset, forward through the day.
   */
  advance(seconds: number): number {
    const w = this.watching;
    if (w) {
      if (w.playing) w.elapsed = Math.min(this.length, w.elapsed + Math.max(0, seconds));
      this.hour = ((24 * w.elapsed) / this.length) % 24;
      return this.hour;
    }
    const ahead = (((this.target - this.hour) % 24) + 24) % 24;
    const step = Math.min(ahead, Math.max(0, seconds) * HOURS_PER_S);
    this.hour = step >= ahead ? this.target : (this.hour + step) % 24;
    return this.hour;
  }
}

/** An hour taken round the clock into 0 to 24. */
const mod24 = (h: number): number => ((h % 24) + 24) % 24;

/** The moon's lit share at an age, days (0 new, 1 full), by the mean lunation's phase angle (`SYNODIC_MONTH_DAYS`). */
export function moonLit(ageDays: number, synodicDays: number): number {
  return (1 - Math.cos((2 * Math.PI * ageDays) / synodicDays)) / 2;
}
